import { useState } from 'react';
import { cn } from '@/utils/classnames';
import { copyToClipboard } from '@/utils/copy';
import { MaterialIcon } from './MaterialIcon';

export interface CodeBlockProps {
  children: string;
  /** Visual variant — "panel" is the standard dark panel; "deep" is the darker JSON variant. */
  variant?: 'panel' | 'deep';
  /** Optional label rendered as a small chip in the top-left. */
  label?: string;
  className?: string;
}

const VARIANT_BG: Record<NonNullable<CodeBlockProps['variant']>, string> = {
  panel: 'bg-[#1E1E1E]',
  deep: 'bg-[#14233D]',
};

export function CodeBlock({ children, variant = 'panel', label, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    const ok = await copyToClipboard(children);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }

  return (
    <div className={cn('relative overflow-hidden rounded-lg', VARIANT_BG[variant], className)}>
      {label ? (
        <span className="absolute left-stack-md top-stack-sm font-label-caps text-label-caps text-[#9CA3AF]">
          {label}
        </span>
      ) : null}
      <button
        type="button"
        onClick={onCopy}
        className={cn(
          'absolute right-stack-sm top-stack-sm z-10',
          'inline-flex items-center gap-unit rounded px-stack-sm py-unit',
          'font-label-caps text-label-caps uppercase tracking-wide',
          'bg-white/5 text-[#E5E7EB] hover:bg-white/10 transition-colors duration-150',
        )}
        aria-label="复制代码"
      >
        <MaterialIcon name={copied ? 'check' : 'content_copy'} size={14} />
        {copied ? 'COPIED' : 'COPY'}
      </button>
      <pre
        className={cn(
          'overflow-x-auto px-container-padding py-stack-md font-code-md text-code-md text-[#E5E7EB]',
          label ? 'pt-stack-md mt-stack-md' : '',
        )}
      >
        <code className="font-code-md">{children}</code>
      </pre>
    </div>
  );
}
